import React, { FC } from 'react';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { GetApplicationData, HardOfferData } from 'handlers/applicationData';

import TradelineRow from './components/TradelineRow';
import LoanBreakdownLine from './components/LoanBreakdownLine';

import styles from './LoanBreakdown.module.scss';

interface Props {
  application: GetApplicationData;
  container?: string;
  title?: string;
  subtitle?: string;
  showFees?: boolean;
}

const LoanBreakdown: FC<Props> = ({ application, container, title, subtitle, showFees = true }) => {
  const fees = (application.documentaryStampTaxFee || 0) + application.originationFee!;
  const formattedFees = formatMonetaryAmount(fees, true);

  let totalLoanAmount = application.calculatedLoanAmount;
  if (!showFees) {
    totalLoanAmount! -= fees;
  }
  const formattedTotalLoanAmount = formatMonetaryAmount(totalLoanAmount, true);

  const hardOffer = application?.hardOffer as HardOfferData;
  const payOff = hardOffer?.payOff || [];
  const keepIt = hardOffer?.keepIt || [];
  const payOffTotal = hardOffer?.payOffTotal || {};

  const tradelinesToPay = [...payOff, ...keepIt].filter((tradeline) => tradeline.weWillPay) || [];
  const tradelinesWeWillSendYouToPay =
    [...payOff, ...keepIt].filter((tradeline) => !tradeline.weWillPay && tradeline.selectedForConsolidation) || [];
  const amountWeWillPay = tradelinesToPay.reduce((sum, tradeline) => sum + tradeline.balance, 0);
  const amountWeWillSendYouToPay = tradelinesWeWillSendYouToPay.reduce((sum, tradeline) => sum + tradeline.balance, 0);

  const isLoanAmountOffSync = payOffTotal?.amount !== application.loanAmount;

  return (
    <div className={container}>
      <>
        {title && <p className={styles.title}>{title}</p>}
        <div className={styles.totalLoanAmountContainer}>
          <p className={styles.termTitle}>{subtitle || 'Total Loan Amount'}</p>
          <p className={styles.termValue}>{formattedTotalLoanAmount}</p>
        </div>
        <div className={styles.loanBreakdownDataContainer}>
          {amountWeWillPay > 0 && !isLoanAmountOffSync && (
            <LoanBreakdownLine
              lineContent={
                <div className={styles.loanBreakdownLine}>
                  <p className={styles.termTitle}>We Will Pay</p>
                  <p className={styles.termValue}>{formatMonetaryAmount(amountWeWillPay, false)}</p>
                </div>
              }
              expandedContent={
                <>
                  <p>After your loan is approved, we will pay off the following debts directly.</p>
                  {tradelinesToPay.map((tradeline) => (
                    <TradelineRow key={tradeline.methodAccountId} tradeline={tradeline} />
                  ))}
                </>
              }
            />
          )}
          {amountWeWillSendYouToPay > 0 && !isLoanAmountOffSync && (
            <LoanBreakdownLine
              lineContent={
                <div className={styles.loanBreakdownLine}>
                  <p className={styles.termTitle}>We Will Send You To Pay</p>
                  <p className={styles.termValue}>{formatMonetaryAmount(amountWeWillSendYouToPay, false)}</p>
                </div>
              }
              expandedContent={
                <>
                  <p>
                    We will send these funds to your primary checking account after your loan is approved. You will use
                    these funds to pay off the following debts.
                  </p>
                  {tradelinesWeWillSendYouToPay.map((tradeline) => (
                    <TradelineRow key={`${tradeline.balance}${tradeline.opened}`} tradeline={tradeline} />
                  ))}
                </>
              }
            />
          )}
          {showFees && (
            <LoanBreakdownLine
              lineContent={
                <div className={styles.loanBreakdownLine}>
                  <p className={styles.termTitle}>Fees</p>
                  <p className={styles.termValue}>{formattedFees}</p>
                </div>
              }
              expandedContent={
                <>
                  <p>
                    This include any applicable state fees and a 1% origination fee that covers our costs. The fees are
                    added to the final loan amount and are not charged upfront.
                  </p>
                  <div className={styles.tradelineContainer}>
                    <p className={styles.termTitle}>Origination Fee</p>
                    <p className={styles.termValue}>{formatMonetaryAmount(application.originationFee, true)}</p>
                  </div>
                  {application.documentaryStampTaxFee && (
                    <div className={styles.tradelineContainer}>
                      <p className={styles.termTitle}>Florida State Fee</p>
                      <p className={styles.termValue}>
                        {formatMonetaryAmount(application.documentaryStampTaxFee, true)}
                      </p>
                    </div>
                  )}
                </>
              }
            />
          )}
        </div>
      </>
    </div>
  );
};

export default LoanBreakdown;
